import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { trackEvent } from '../lib/analytics';

// ── Nav icon components (inline SVG, no external dep) ─────────────

function IconHome() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
      <polyline points="9 21 9 12 15 12 15 21" />
    </svg>
  );
}

function IconJobs() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="8" y1="15" x2="16" y2="15" />
    </svg>
  );
}

function IconEarnings() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}


function IconMore() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="5" cy="12" r="1" />
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
    </svg>
  );
}

function IconPlus() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

// ── More sheet secondary nav items ────────────────────────────────

const MORE_ITEMS = [
  { to: '/analytics',   icon: '📊', label: 'Analytics' },
  { to: '/assignments', icon: '📋', label: 'Assignments' },
  { to: '/portfolio',   icon: '🏆', label: 'Portfolio' },
  { to: '/tools',       icon: '🔧', label: 'Tools' },
  { to: '/time',        icon: '⏱',  label: 'Time' },
  { to: '/training',    icon: '🎓', label: 'Training' },
  { to: '/integrations',icon: '🎙', label: 'Voice & MCP' },
  { to: '/settings',    icon: '⚙️', label: 'Settings' },
] as const;

const MORE_PATHS = MORE_ITEMS.map((i) => i.to);

// ── Shell ─────────────────────────────────────────────────────────

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreDialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = moreDialog.current;
    if (moreOpen && dialog && !dialog.open) {
      dialog.showModal();
      dialog.querySelector<HTMLButtonElement>('button')?.focus();
    }
    if (!moreOpen && dialog?.open) dialog.close();
  }, [moreOpen]);

  // Highlight "More" button when on any secondary page
  const moreActive = MORE_PATHS.some((p) => location.pathname.startsWith(p));

  function closeMore() { setMoreOpen(false); }

  function toggleMore() {
    const nextOpen = !moreOpen;
    trackEvent('more_menu_toggled', { open: nextOpen });
    setMoreOpen(nextOpen);
  }

  return (
    <div className="frp-shell">
      <div className="frp-page-content">
        <Outlet />
        <footer className="frp-technology-credit">
          <p>GearGenius is a Smarter Way to Apply technology.</p>
          <a href="https://smarterwaytoapply.com/" target="_blank" rel="noopener noreferrer">
            SmarterWayToApply.com
          </a>
        </footer>
      </div>

      <dialog
        ref={moreDialog}
        id="frp-more-pages"
        className="frp-more-sheet"
        aria-label="More pages"
        data-open={moreOpen ? 'true' : 'false'}
        onCancel={closeMore}
        onClose={closeMore}
        onClick={(event) => { if (event.target === event.currentTarget) closeMore(); }}
        onKeyDown={(event) => {
          if (event.key !== 'Tab') return;
          const controls = [...event.currentTarget.querySelectorAll<HTMLElement>('button, a[href]')];
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }}
      >
        <button type="button" className="frp-btn frp-btn-ghost frp-more-close" onClick={closeMore}>
          Close menu
        </button>
        <div className="frp-more-grid">
          {MORE_ITEMS.map(({ to, icon, label }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => {
                trackEvent('more_menu_navigation', { destination: to });
                closeMore();
              }}
              className={({ isActive }) =>
                `frp-more-item${isActive ? ' active' : ''}`
              }
            >
              <span className="frp-more-item-icon">{icon}</span>
              <span>{label}</span>
            </NavLink>
          ))}
          <a
            href="/"
            className="frp-more-item"
            aria-label="Back to GearGenius suite home"
            onClick={() => trackEvent('suite_home_opened')}
          >
            <span className="frp-more-item-icon">🏠</span>
            <span>Suite Home</span>
          </a>
        </div>
      </dialog>

      {/* Bottom navigation */}
      <nav className="frp-bottom-nav" aria-label="Main navigation">
        <div className="frp-nav-items">
          <NavLink to="/home" className={({ isActive }) => `frp-nav-item${isActive ? ' active' : ''}`}>
            <IconHome />
            <span className="frp-nav-label">Home</span>
          </NavLink>

          <NavLink to="/jobs" className={({ isActive }) => `frp-nav-item${isActive ? ' active' : ''}`}>
            <IconJobs />
            <span className="frp-nav-label">Jobs</span>
          </NavLink>

          {/* Center slot — FAB sits above this */}
          <div className="frp-nav-fab-slot" aria-hidden="true" />

          <NavLink to="/earnings" className={({ isActive }) => `frp-nav-item${isActive ? ' active' : ''}`}>
            <IconEarnings />
            <span className="frp-nav-label">Earnings</span>
          </NavLink>

          <button
            type="button"
            className={`frp-nav-item${moreActive || moreOpen ? ' active' : ''}`}
            aria-haspopup="dialog"
            aria-controls="frp-more-pages"
            aria-expanded={moreOpen}
            onClick={toggleMore}
          >
            <IconMore />
            <span className="frp-nav-label">More</span>
          </button>
        </div>
      </nav>

      {/* Floating action button — Log Job */}
      <button
        type="button"
        className="frp-fab"
        aria-label="Log a job"
        onClick={() => {
          trackEvent('log_job_fab_clicked');
          navigate('/log-job');
        }}
      >
        <IconPlus />
      </button>
    </div>
  );
}
